import type {
  ComparisonRow,
  FlowStage,
  InsightCopy,
  Plan,
  SolutionGroup,
} from '@/features/marketing/content';

/**
 * Copy comercial del sitio público en español, transcrito del `index.html`
 * publicado en `ilumetria.com`. Es el **texto original**: los demás idiomas se
 * traducen desde aquí.
 *
 * Los precios NO están en este archivo — viven en `features/marketing/content.ts`
 * porque son decisiones de negocio y no deben poder divergir entre idiomas.
 */

export const marketing = {
  hero: {
    eyebrow: 'ILUMETRIA',
    titulo: 'El <g>Google Analytics</g> para eventos físicos',
    lead: 'Transformamos cada interacción en oportunidades de negocio.',
    apoyo:
      'La primera plataforma de inteligencia de datos que ilumina el ROI de tus eventos presenciales con IoT e IA.',
    ctaPrimary: 'Agendar demo',
    ctaSecondary: 'Ver soluciones y planes',
    videoLabel: 'Demostración de la plataforma ILUMETRIA',
    videoFallback: 'Tu navegador no puede reproducir este video.',
  },

  /**
   * Reconocimiento LAS 100 PRO 2026, el anuncio del pie del sitio.
   *
   * Está escrito para el lugar donde se lee: un pie de página que alguien
   * recorre al final. Cuatro líneas, sin exclamaciones y sin un botón que pida
   * el clic —la noticia, qué es el reconocimiento, la premiación con su fecha, y
   * los agradecimientos—. El borrador es de la casa; es copy comercial y Alan
   * puede reescribirlo sin tocar nada más.
   *
   * A propósito **no afirma tracción, clientes ni cifras**: nada de eso está
   * publicado en el sitio y un reconocimiento no es el lugar para estrenarlo.
   *
   * La fecha y la imagen no están aquí: viven en
   * `components/layout/datosDelAnuncio.ts` para que no puedan divergir entre
   * idiomas. Había además una liga de registro, con su texto de enlace; Alan la
   * quitó el 2026-09-21 y se fue de los dos diccionarios a la vez.
   */
  reconocimiento: {
    titulo: 'Reconocidos como una de LAS 100 PRO 2026',
    logoAlt: 'Distintivo de LAS 100 PRO 2026',
    cuerpo:
      'Una noticia que nos llena de orgullo: LAS 100 PRO distingue a las startups mexicanas con mayor potencial de crecimiento, innovación e impacto. Nos confirma que el problema que elegimos —medir lo que de verdad pasa dentro de un evento— importa, y nos obliga a llevarlo a más recintos y a más marcas.',
    premiacionEtiqueta: 'Premiación:',
    /** Sin letras a propósito: separa los dos datos de la premiación. */
    premiacionSeparador: ' · ',
    premiacionLugar: 'Teatro de la UDEM',
    gracias:
      'Gracias a PRO NETWORK por impulsar esta iniciativa y a Hey Banco por hacer posible esta séptima edición. Y gracias a nuestro equipo, a nuestros clientes y a los aliados, mentores e inversionistas que apostaron cuando esto era sólo una hipótesis.',
  },

  insight: {
    eyebrow: 'El problema',
    headline: 'Eliminamos el agujero negro de los eventos físicos',
    question: '¿Sabes qué porcentaje de tus visitantes se fue sin dejar sus datos?',
    answer:
      'Capturamos y calificamos tus leads pasivos basándonos en el perfil de los asistentes y la detección de sus emociones al interactuar con tu marca.',
    donutLabel: 'Leads pasivos',
    legendPassive: (pct: number) => `${pct}% leads pasivos (hoy invisibles)`,
    legendScanned: (pct: number) => `${pct}% leads escaneados`,
    chartAlt: (pct: number) =>
      `${pct}% de los visitantes son leads pasivos que hoy no dejan sus datos`,
  } satisfies InsightCopy & { eyebrow: string },

  flow: {
    eyebrow: 'Flujo de valor',
    title: 'Acompañamiento de inicio a fin',
    lead: 'Antes, durante y después del evento: cada etapa alimenta a la siguiente con datos reales.',
    tablistLabel: 'Etapas del evento',
  },

  flowStages: [
    {
      id: 'pre',
      step: '01',
      shortLabel: 'PRE',
      title: 'PRE-EVENTO: Matchmaking Inteligente',
      image: '/recursos/matchmaking.png',
      imageAlt: 'Pantalla de matchmaking entre marcas y eventos en la plataforma ILUMETRIA',
      blocks: [
        {
          title: 'Para Marcas',
          body: 'Nuestra plataforma te recomienda los eventos ideales en tu zona en los que tu marca debería participar con estimaciones de ROI basadas en la reputación del evento, el perfilamiento y análisis histórico de sentimiento de asistentes.',
        },
        {
          title: 'Para Organizadores',
          body: 'Gana exposición de tus eventos con marcas y permite que te hagan ofertas para patrocinar tu evento o determina tus tarifas directamente.',
        },
      ],
    },
    {
      id: 'durante',
      step: '02',
      shortLabel: 'DURANTE',
      title: 'DURANTE EL EVENTO: IoT + IA en acción',
      image: '/recursos/stand_iot.png',
      imageAlt: 'Stand equipado con antena de lectura y módulo de visión computacional',
      blocks: [
        {
          title: 'Captura pasiva de leads',
          body: 'Brindamos a cada asistente un gafete o pulsera con una etiqueta inteligente, y a cada stand una antena de lectura capaz de medición submétrica.',
        },
        {
          title: 'Touch to Collect',
          body: 'El asistente intercambia información digital con un toque con tecnología NFC, convirtiéndose en un lead activo y legalmente autorizado.',
        },
        {
          title: 'Análisis de emociones',
          body: 'Nuestra IA analiza las micro-expresiones de los asistentes cuando interactúan con tu stand para calificar el interés real.',
        },
      ],
      note: 'Todas nuestras tecnologías son Plug & Play, así que olvídate de tediosas, costosas y tardadas instalaciones con un técnico.',
    },
    {
      id: 'post',
      step: '03',
      shortLabel: 'POST',
      title: 'POST-EVENTO: El Dashboard de Atribución',
      image: '/recursos/dashboard_post.png',
      imageAlt: 'Dashboard de atribución con reportes de desempeño posteriores al evento',
      blocks: [
        {
          title: 'Reportes detallados',
          body: 'Nuestra plataforma te entrega reportes detallados del desempeño de tu marca en el evento con mapas contextuales y de calor; así como de generación de leads pasivos y activos.',
        },
        {
          title: 'Recomendaciones inteligentes',
          body: 'Te brindamos recomendaciones para optimizar tus resultados en tu próximo patrocinio así como un listado de próximos eventos donde tienes buena probabilidad de ROIs altos.',
        },
      ],
    },
  ] satisfies FlowStage[],

  solutions: {
    eyebrow: 'Maximiza tu impacto',
    title: 'Resultados reales para organizadores y marcas',
  },

  solutionGroups: [
    {
      id: 'organizadores',
      audience: 'Para Organizadores',
      image: '/recursos/organizer_dashboard.png',
      imageAlt: 'Panel de organizador con métricas de aforo y tráfico del recinto',
      items: [
        {
          id: 'certificacion',
          title: 'Certificación de Audiencia',
          body: 'Convierte tu recinto en un Smart Venue. Atrae mejores patrocinadores con métricas de tráfico y aforo en tiempo real.',
        },
        {
          id: 'multitudes',
          title: 'Gestión de Multitudes',
          body: 'Visualiza cuellos de botella y zonas frías. Reúne métricas valiosas para la planeación y seguridad.',
        },
        {
          id: 'retencion',
          title: 'Retención de Marcas',
          body: 'Demuestra con datos que tu evento es altamente rentable, asegurando la inversión de tus expositores año tras año.',
        },
      ],
    },
    {
      id: 'marcas',
      audience: 'Para Marcas / Expositores',
      image: '/recursos/exhibitor_dashboard.png',
      imageAlt: 'Panel de marca con leads capturados y atribución de ROI',
      items: [
        {
          id: 'roi',
          title: 'Maximización de ROI',
          body: 'Invierte de forma inteligente. Nuestro panel te muestra qué eventos generarán los resultados que necesitas.',
        },
        {
          id: 'leads-pasivos',
          title: 'Visibilidad de Leads Pasivos',
          body: 'Obtén perfiles de audiencia ultra precisos. Califica prospectos automáticamente analizando su reacción emocional.',
        },
        {
          id: 'atribucion',
          title: 'Atribución Directa',
          body: 'Conecta cada peso de tu inversión en el stand con leads cálidos directamente integrados en tu CRM.',
        },
      ],
    },
  ] satisfies SolutionGroup[],

  pricing: {
    eyebrow: 'Nuestros planes',
    title: 'Selecciona tu inversión inteligente',
    lead: 'Suscripción para encontrar los eventos correctos, y hardware por evento para medir lo que pasa dentro de ellos.',
    featuredTag: 'Más elegido',
    free: 'Gratis',
    /** En español el precio va tal cual: la moneda local se sobreentiende. */
    formatoPrecio: (precio: string) => precio,
    freeNote: 'Sin costo',
    customNote:
      '¿Necesitas algo especial? Personalizamos la infraestructura para eventos de más de 100 stands.',
    customCta: 'Solicitar plan personalizado',
  },

  plans: [
    {
      id: 'free-pass',
      name: 'Free Pass',
      ctaLabel: 'Crear cuenta gratis',
      variants: [
        {
          id: 'marcas',
          label: 'Marcas',
          priceNote: 'Gratis',
          features: [
            'Acceso a listado de eventos en tu región',
            'Matchmaking de 1 evento al mes con analítica de ROI y perfilamiento de asistentes',
            'Acceso a histórico de desempeño en eventos limitado a 2 eventos asistidos',
          ],
        },
        {
          id: 'organizadores',
          label: 'Organizadores',
          priceNote: 'Gratis',
          features: [
            'Publicación libre de tus eventos',
            'Matchmaking con hasta 3 patrocinadores potenciales de 1 evento al mes con analítica de ROI y recomendaciones de ticket promedio por patrocinador',
            'Acceso a histórico de desempeño en eventos con portal de organizador limitado a 2 eventos previos',
            'Acceso a promoción de evento en plataforma',
          ],
        },
      ],
    },
    {
      id: 'smart-match',
      name: 'Suscripción Smart Match',
      featured: true,
      ctaLabel: 'Suscribirse',
      secondaryCtaLabel: 'Agendar DEMO',
      variants: [
        {
          id: 'marcas',
          label: 'Marcas',
          priceNote: 'Costo mensual',
          description:
            'Para marcas o expositores que buscan participar en los mejores eventos maximizando SU ROI.',
          features: [
            'Acceso a listado de eventos en tu región',
            'Matchmaking de hasta 10 eventos al mes en tu región y acceso a analítica de ROI y perfilamiento de asistentes para todos los eventos listados en plataforma',
            'Acceso a todo tu histórico de desempeño en eventos asistidos',
            'Capacidad de realizar ofertas personalizadas de patrocinio a los organizadores de todos los eventos listados en plataforma',
            'Descuentos sobre renta de Hardware con base en frecuencia de patrocinio de eventos',
          ],
        },
        {
          id: 'organizadores',
          label: 'Organizadores',
          priceNote: 'Costo mensual',
          features: [
            'Publicación libre de tus eventos',
            'Certificado de calidad en plataforma basado en métricas de análisis emocional de asistentes y calificación de patrocinadores anteriores',
            'Matchmaking con hasta 10 patrocinadores potenciales de hasta 5 eventos al mes con analítica de ROI y recomendaciones de ticket promedio por patrocinador',
            'Acceso a todo tu histórico de desempeño en eventos con portal de organizador',
            'Descuentos sobre renta de Hardware con base en frecuencia de patrocinio de eventos',
            'Acceso a promoción de evento en plataforma con descuentos por frecuencia de organización de eventos',
          ],
        },
      ],
    },
    {
      id: 'smart-partner',
      name: 'Plan Smart Partner para Organizadores',
      ctaLabel: 'Contratar',
      secondaryCtaLabel: 'Agendar DEMO',
      variants: [
        {
          id: 'un-dia',
          label: '1 día',
          priceNote: 'Costo 1 día',
          description:
            'Nuestra solución integral para digitalizar todo el recinto. Un modelo donde el organizador recupera la inversión y aporta gran valor a sus patrocinadores.',
          features: [
            'Infraestructura HaaS: 5 antenas de precisión submétrica y 5 módulos de visión computacional de fácil instalación en stands para captación de leads pasivos y análisis de emociones de asistentes. Cada stand requiere 1 antena y 1 módulo de visión computacional; todas nuestras tecnologías son Plug & Play para una fácil instalación.',
            'Paquete de 100 etiquetas inteligentes para tus asistentes (1 etiqueta por asistente) con módulo de carga de datos de rápida implementación a entrada de tu evento',
            'Acceso a funciones de la plataforma sobre seguimiento de métricas y generación de reportes para hasta 5 patrocinadores y 1 organizador',
          ],
        },
        {
          id: 'tres-dias',
          label: '3 días',
          priceNote: 'Costo 3 días',
          description:
            'Nuestra solución integral para digitalizar todo el recinto. Un modelo donde el organizador recupera la inversión y aporta gran valor a sus patrocinadores.',
          features: [
            'Infraestructura HaaS: 5 antenas de precisión submétrica y 5 módulos de visión computacional de fácil instalación en stands para captación de leads pasivos y análisis de emociones de asistentes. Cada stand requiere 1 antena y 1 módulo de visión computacional; todas nuestras tecnologías son Plug & Play para una fácil instalación.',
            'Paquete de 100 etiquetas inteligentes para tus asistentes (1 etiqueta por asistente) con módulo de carga de datos de rápida implementación a entrada de tu evento',
            'Acceso a funciones de la plataforma sobre seguimiento de métricas y generación de reportes para hasta 5 patrocinadores y 1 organizador',
          ],
        },
      ],
    },
    {
      id: 'solo-brand',
      name: 'Plan Solo Brand',
      ctaLabel: 'Contratar',
      secondaryCtaLabel: 'Agendar DEMO',
      variants: [
        {
          id: 'un-dia',
          label: '1 día',
          priceNote: 'Costo 1 día',
          description:
            'Especial para las marcas que quieren métricas de su evento sin depender de los organizadores.',
          features: [
            'Infraestructura HaaS: 2 módulos de visión computacional de fácil instalación para tu stand para conteo de potenciales leads pasivos y análisis de emociones de asistentes. Se necesitan 2 por stand; todas nuestras tecnologías son Plug & Play para una fácil instalación.',
            'Acceso a funciones de la plataforma sobre seguimiento de métricas y generación de reportes para tu marca',
          ],
        },
        {
          id: 'tres-dias',
          label: '3 días',
          priceNote: 'Costo 3 días',
          description:
            'Especial para las marcas que quieren métricas de su evento sin depender de los organizadores.',
          features: [
            'Infraestructura HaaS: 2 módulos de visión computacional de fácil instalación para tu stand para conteo de potenciales leads pasivos y análisis de emociones de asistentes. Se necesitan 2 por stand; todas nuestras tecnologías son Plug & Play para una fácil instalación.',
            'Acceso a funciones de la plataforma sobre seguimiento de métricas y generación de reportes para tu marca',
          ],
        },
      ],
    },
  ] satisfies Plan[],

  comparison: {
    eyebrow: 'Comparativa',
    title: 'Qué mide cada plan de hardware',
    colFeature: 'Característica',
    colSoloBrand: 'Plan Solo Brand',
    colSmartPartner: 'Plan Smart Partner para Organizadores',
  },

  comparisonRows: [
    {
      id: 'trafico',
      feature: 'Conteo de Tráfico',
      soloBrand: 'Visual (Estimado)',
      smartPartner: 'Exacto (Individualizado)',
    },
    {
      id: 'dwell-time',
      feature: 'Dwell Time (Permanencia)',
      soloBrand: 'Limitado al área de cámara',
      smartPartner: 'Trazabilidad total en el stand',
    },
    {
      id: 'emociones',
      feature: 'Análisis de Emociones',
      soloBrand: 'Incluido',
      smartPartner: 'Incluido',
    },
    {
      id: 'lead-pasivo',
      feature: 'Captación de lead pasivo',
      soloBrand: 'No disponible',
      smartPartner: 'Sí (Datos del "Clon Virtual")',
      soloBrandMissing: true,
    },
    {
      id: 'nfc',
      feature: 'Touch to Collect (NFC)',
      soloBrand: 'No disponible',
      smartPartner: 'Sí (Intercambio digital)',
      soloBrandMissing: true,
    },
  ] satisfies ComparisonRow[],

  partners: {
    eyebrow: 'Aprobación de la industria',
    title: 'Marcas que confían en el futuro',
  },

  finalCta: {
    headline: '¿Listo para iluminar tu próximo evento?',
    body: 'Únete a las marcas que ya están midiendo el éxito en el mundo real.',
    ctaLabel: 'Contacta a nuestro equipo de ventas',
  },
  /**
   * Aviso del estudio: una notificacion temporal en la home.
   *
   * Se apaga con `ESTUDIO_ABIERTO` en `features/estudio/campana.ts`. El texto
   * no menciona el producto: invita al estudio, no a la plataforma.
   */
  avisoEstudio: {
    titulo: 'Estamos levantando un estudio de la industria',
    cuerpo:
      'Cuéntanos cómo mides tus eventos y te enviamos el reporte completo con los resultados de todo el sector.',
    cta: 'Ver de qué se trata',
    duracion: '6-8 min',
    cerrar: 'Cerrar el aviso',
  },

};
